import React from 'react';
import { useToast } from '../Toast/ToastContext';

class TradingErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for monitoring
    console.error('TradingErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to external monitoring service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }

    // Show error toast if showToast prop is provided
    if (this.props.showToast) {
      this.props.showToast(`Trading Error: ${error.message}`, 'error');
    }
  }

  handleRetry = () => {
    const { maxRetries = 3 } = this.props;

    if (this.state.retryCount < maxRetries) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: this.state.retryCount + 1
      });
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    });
  };

  render() {
    if (this.state.hasError) {
      const { maxRetries = 3, componentName = 'Trading Component' } = this.props;
      const canRetry = this.state.retryCount < maxRetries;

      return (
        <div className="trading-error-boundary">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Trading Component Error
            </h4>
            <p>
              <strong>{componentName}</strong> encountered an error and couldn't be displayed properly.
            </p>
            <p className="mb-3">
              <small>Error: {this.state.error && this.state.error.toString()}</small>
            </p>

            <div className="d-flex gap-2">
              {canRetry && (
                <button
                  className="btn btn-warning btn-sm"
                  onClick={this.handleRetry}
                >
                  <i className="fas fa-redo me-1"></i>
                  Retry ({this.state.retryCount}/{maxRetries})
                </button>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={this.handleReset}
              >
                <i className="fas fa-refresh me-1"></i>
                Reset Component
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-sync me-1"></i>
                Reload Page
              </button>
            </div>

            {/* Fallback functionality for critical trading operations */}
            <hr />
            <div className="mt-3">
              <h6>Alternative Actions:</h6>
              <div className="d-flex flex-wrap gap-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => window.open('/positions', '_blank')}
                >
                  View Positions
                </button>
                <button
                  className="btn btn-outline-success btn-sm"
                  onClick={() => window.open('/home', '_blank')}
                >
                  New Home
                </button>
                <button
                  className="btn btn-outline-info btn-sm"
                  onClick={() => alert('Please contact support with error details')}
                >
                  Contact Support
                </button>
              </div>
            </div>
          </div>

          {/* Development error details */}
          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <details className="mt-3">
              <summary className="btn btn-outline-secondary btn-sm">
                Show Error Details (Development)
              </summary>
              <pre className="mt-2 p-2 bg-light border rounded" style={{ fontSize: '12px', overflow: 'auto' }}>
                {this.state.error && this.state.error.stack}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC wrapper for functional components that need error boundary with toast
export const withTradingErrorBoundary = (WrappedComponent, options = {}) => {
  return function TradingErrorBoundaryWrapper(props) {
    const { showError } = useToast();

    return (
      <TradingErrorBoundary
        showToast={showError}
        componentName={options.componentName || WrappedComponent.displayName || WrappedComponent.name}
        maxRetries={options.maxRetries || 3}
        {...options}
      >
        <WrappedComponent {...props} />
      </TradingErrorBoundary>
    );
  };
};

export default TradingErrorBoundary;
